/**
 * RFC 3986 準拠のASCII文字のみを対象としたURL検出パターン
 *
 * @description
 * 厳密な検出器ではありません。RFC 3986 非準拠の文字を除外するためのパターンです。
 * */
const URL_REGEX = /(https?:\/\/[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]+)/

/** Link_Term=Soft に該当する末尾文字 (ASCII subset) + シングルクォート */
const TRAILING_SOFT_REGEX = /[.,;:!?']+$/

/**
 * Link_Term=Open/Close に該当する括弧ペア (ASCII subset)。
 *
 * @description
 * UTS #58 では Bidi_Paired_Bracket_Type に基づくが、ここでは ASCII URL で出現しうるペアのみ対象。
 */
const BRACKET_PAIRS = [
  ['(', ')'],
  ['[', ']'],
] as const

/**
 * URL 末尾の Link_Term=Soft 文字と不均衡な Link_Term=Close 文字を除去する。
 *
 * @description
 * UTS #58 Section 3.5 Termination Algorithm の簡易実装。
 *
 * 以下のルールを収束するまで繰り返し適用する:
 * 1. 末尾の Soft 文字 (.,;:!?') を除去
 * 2. 末尾の Close 文字 ()/]) について、対応する Open が不足している場合のみ除去
 * @see {@link https://www.unicode.org/reports/tr58/#Link_Term_Property UTS #58 (Unicode Link Detection and Formatting)}
 */
export function trimUrl(url: string): { trimmed: string; trailing: string } {
  let trimmed = url
  let trailing = ''

  let changed = true
  while (changed) {
    changed = false

    const softMatch = trimmed.match(TRAILING_SOFT_REGEX)
    if (softMatch) {
      trailing = softMatch[0] + trailing
      trimmed = trimmed.slice(0, -softMatch[0].length)
      changed = true
    }

    for (const [open, close] of BRACKET_PAIRS) {
      while (trimmed.endsWith(close)) {
        const openCount = (trimmed.match(new RegExp(`\\${open}`, 'g')) || []).length
        const closeCount = (trimmed.match(new RegExp(`\\${close}`, 'g')) || []).length
        if (closeCount > openCount) {
          trailing = close + trailing
          trimmed = trimmed.slice(0, -1)
          changed = true
        } else {
          break
        }
      }
    }
  }

  return { trimmed, trailing }
}

/**
 * テキストを URL 部分とそれ以外に分割し、UTS #58 に基づく終端処理を適用した結果を返す。
 *
 * @description
 * - `String#split(URL_REGEX)` で分割後、URL パーツに `trimUrl` を適用する。
 * - 除去された末尾文字（Soft/Close）は隣接するテキストパーツの先頭に結合される。
 * @see {@link https://www.unicode.org/reports/tr58/ UTS #58 (Unicode Link Detection and Formatting)}
 */
export function splitByUrls(content: string): Array<{ type: 'text' | 'url'; value: string }> {
  const rawParts = content.split(URL_REGEX)
  const result: Array<{ type: 'text' | 'url'; value: string }> = []

  for (let i = 0; i < rawParts.length; i++) {
    const part = rawParts[i]

    if (URL_REGEX.test(part)) {
      const { trimmed, trailing } = trimUrl(part)
      result.push({ type: 'url', value: trimmed })

      if (!trailing) continue

      const next = rawParts[i + 1]
      if (next !== undefined) {
        rawParts[i + 1] = trailing + next
      } else {
        result.push({ type: 'text', value: trailing })
      }
    } else if (part) {
      result.push({ type: 'text', value: part })
    }
  }

  return result
}
